import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import { MutationOptions, useMutation } from '@tanstack/react-query';
import { downloadListFile } from 'Services';
import { callAll } from 'Utils';
import { useListFilesDownloadStore } from 'Hooks/zustand/useListFilesDownloadStore';

type HookArgs = {
    id: string;
    mutationOptions?: Omit<MutationOptions<AxiosResponse, unknown, unknown, unknown>, 'mutationKey'>;
};

type InitiateFileDownloadResponse = {
    totalParts: number;
    fileIntegrityHash: string;
};

const useDownloadListFile = ({ id, mutationOptions }: HookArgs) => {
    const { t } = useTranslation();
    const { addAlert } = useAlertBanner();
    const filesDownloadStore = useListFilesDownloadStore();
    const currentDownload = filesDownloadStore.getFilesDownloading().find((downloadItem) => downloadItem.id === id);

    const defaultNetworkErrorHandler = () => {
        filesDownloadStore.removeFileFromDownloading(id);
        addAlert({
            id: 'download-list-files-error',
            status: 'danger',
            content: t('File failed to download due to a network error')
        });
    };

    const defaultSuccessHandler = (
        data: InitiateFileDownloadResponse | ArrayBuffer,
        variables: DownloadListFileSchema
    ) => {
        const { isInitiateDownload } = variables;
        if (!isInitiateDownload && data) {
            const partData = data as ArrayBuffer;
            if (partData.byteLength > 0 && currentDownload) {
                filesDownloadStore.updateFileDownloadState({
                    ...currentDownload,
                    fileParts: [...currentDownload.fileParts, new Uint8Array(partData)],
                    partCompleted: (currentDownload.partCompleted ?? 0) + 1
                });
            } else {
                defaultNetworkErrorHandler();
            }
        } else {
            const { totalParts, fileIntegrityHash } = data as InitiateFileDownloadResponse;
            if (currentDownload)
                filesDownloadStore.updateFileDownloadState({
                    ...currentDownload,
                    totalParts,
                    partCompleted: 0,
                    fileIntegrityHash
                });
        }
    };

    return useMutation(
        ({ fileName, processType, isInitiateDownload, partNum }) =>
            downloadListFile({ fileName, processType, isInitiateDownload, partNum }),
        {
            onSuccess: callAll(defaultSuccessHandler, mutationOptions?.onSuccess),
            onError: callAll(defaultNetworkErrorHandler, mutationOptions?.onError)
        }
    );
};

export default useDownloadListFile;
